import React, { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useNavigate } from 'react-router'
import AuthHeader from 'src/components/molecules/AuthHeader'
import AuthWrapper from 'src/enviroment/layouts/auth-wrapper'
import { Controller, FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import { z } from 'zod'
import { signUpSchema } from 'src/enviroment/validation/schema'
import Typography from 'src/components/atoms/Typography'
import { Input } from 'src/components/atoms/inputs/Input'
import { PrimaryButton } from 'src/components/atoms/buttons/PrimaryButton'
import { Checkbox } from 'src/components/atoms/buttons/CheckBoxButton'
import PasswordStrengthIndicator from 'src/components/atoms/PasswordStrengthIndicator'
import { ROUTES } from 'src/types/routes'
import TermsPrivacy from 'src/components/molecules/TermsPrivacy'

type SignUpFormData = z.infer<typeof signUpSchema>

export interface SingUpTemplateProps {
  onSubmit: SubmitHandler<SignUpFormData>
  isLoading?: boolean
  invitedEmail?: string | null
}

const SingUpTemplate: React.FC<SingUpTemplateProps> = ({ onSubmit, isLoading = false, invitedEmail }) => {
  const navigate = useNavigate()
  const [isPasswordFocused, setIsPasswordFocused] = useState(false)

  const defaultValues = {
    name: '',
    companyName: '',
    email: invitedEmail || '',
    password: '',
    isRemember: false,
    marketingMailing: false,
  } satisfies SignUpFormData

  const methods = useForm<SignUpFormData>({
    defaultValues,
    mode: 'all',
    reValidateMode: 'onChange',
    resolver: zodResolver(signUpSchema),
  })

  const {
    handleSubmit,
    watch,
    formState: { isValid },
  } = methods

  const password = watch('password')

  return (
    <FormProvider {...methods}>
      <Helmet>
        <title>RAMO - Sign Up</title>
      </Helmet>
      <AuthWrapper>
        <div className="space-y-8">
          <AuthHeader title="Sign up to" subtitle="Create an account to see the potential of your landscape." />

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <Controller
              name="name"
              control={methods.control}
              render={({ field: { onChange, ...field }, formState }) => (
                <Input
                  {...field}
                  onChange={onChange}
                  label="Name"
                  autoFocus
                  autoComplete="name"
                  type="text"
                  errorMessage={formState.errors.name?.message}
                />
              )}
            />
            <Controller
              name="companyName"
              control={methods.control}
              render={({ field: { onChange, ...field }, formState }) => (
                <Input
                  {...field}
                  onChange={onChange}
                  label="Organization"
                  autoComplete="organization"
                  type="text"
                  errorMessage={formState.errors.companyName?.message}
                />
              )}
            />
            <Controller
              name="email"
              control={methods.control}
              render={({ field: { onChange, ...field }, formState }) => (
                <Input
                  {...field}
                  onChange={onChange}
                  label="Email Address"
                  autoComplete="new-email"
                  type="email"
                  errorMessage={formState.errors.email?.message}
                />
              )}
            />
            <Controller
              name="password"
              control={methods.control}
              render={({ field: { onChange, ...field }, formState }) => (
                <div>
                  <Input
                    {...field}
                    onChange={onChange}
                    onFocus={() => setIsPasswordFocused(true)}
                    label="Password"
                    type="password"
                    autoComplete="new-password"
                    errorMessage={formState.errors.password?.message}
                  />
                  <PasswordStrengthIndicator open={isPasswordFocused || !!password} value={password} />
                </div>
              )}
            />

            <div className="row flex items-center justify-between text-center align-middle">
              <Controller
                name="isRemember"
                control={methods.control}
                render={({ field: { onChange } }) => <Checkbox onCheckedChange={onChange} label="Keep me logged in" />}
              />
            </div>
            <div className="space-y-4">
              <PrimaryButton type="submit" isLoading={isLoading} disabled={!isValid} fullWidth>
                Next
              </PrimaryButton>
            </div>
          </form>
          <div className="row flex items-center justify-between text-center align-middle">
            <Controller
              name="marketingMailing"
              control={methods.control}
              render={({ field: { onChange } }) => (
                <Checkbox onCheckedChange={onChange} label="I consent to receive marketing emails." />
              )}
            />
          </div>
          <div>
            <TermsPrivacy className="text-light-green-50" />
          </div>

          <div className="text-center">
            <Typography variant="base" className="justify-left flex flex-row text-light-green-50">
              Do you already have an account?{' '}
              <Typography
                variant="base-semibold"
                className="ml-2 cursor-pointer text-light-green-500 underline hover:text-light-green-400"
                onClick={() => navigate(ROUTES.SIGN_IN)}
              >
                Log in
              </Typography>
            </Typography>
          </div>
        </div>
      </AuthWrapper>
    </FormProvider>
  )
}

export default SingUpTemplate
